import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, User, Mail, Phone, Building2, Store, FileText, Lock, Eye, EyeOff, KeyRound } from 'lucide-react';
import { apiClient } from '../utils/api';
import { useShopScopedPage } from '../hooks/useShopScopedReload';
import { ShopSelectionGate } from '../components/ShopSelectionGate';
import { useToast } from '../components/Toast';
import { type SupportedPhoneCountry } from '@digitaltattoo/shared-utils';
import { InternationalPhoneInput } from '../components/InternationalPhoneInput';

interface EmployeeFormData {
  firstName: string;
  lastName: string;
  code: string;
  email: string;
  phone: string;
  companyId: string;
  shopIds: string[];
  type: 'VENDOR' | 'SECRETARY' | 'OTHER';
  notes: string;
  isActive: boolean;
  createAccount: boolean;
  password: string;
  confirmPassword: string;
}

export function EmployeeCreatePage() {
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();
  const { needsShopSelection, canLoad, currentShopId } = useShopScopedPage({ requireSingleShop: true });

  const [formData, setFormData] = useState<EmployeeFormData>({
    firstName: '',
    lastName: '',
    code: '',
    email: '',
    phone: '',
    companyId: '',
    shopIds: [],
    type: 'VENDOR',
    notes: '',
    isActive: true,
    createAccount: true,
    password: '',
    confirmPassword: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [companies, setCompanies] = useState<Array<{ id: string; name: string }>>([]);
  const [shops, setShops] = useState<Array<{ id: string; name: string }>>([]);
  const [phoneCountry, setPhoneCountry] = useState<SupportedPhoneCountry>('FR');
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [phoneE164, setPhoneE164] = useState<string | null>(null);
  const [phoneIsValid, setPhoneIsValid] = useState(false);

  useEffect(() => {
    if (!canLoad) return;
    if (currentShopId) {
      setFormData((prev) => ({ ...prev, shopIds: [currentShopId] }));
    }
    apiClient('/api/admin/companies?limit=200')
      .then((r) => r.json())
      .then((d) => { if (d.success) setCompanies((d.data?.companies || []).map((c: any) => ({ id: c.id, name: c.name }))); })
      .catch(console.error);
    apiClient('/api/admin/shops?limit=200')
      .then((r) => r.json())
      .then((d) => { if (d.success) setShops((d.data?.shops || []).map((s: any) => ({ id: s.id, name: s.name }))); })
      .catch(console.error);
  }, [canLoad, currentShopId]);

  const set = (field: keyof EmployeeFormData, value: any) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.firstName || !formData.lastName || !formData.email) {
      setError('Prénom, nom et email sont obligatoires.');
      return;
    }
    if (!/^[A-Z0-9]{4}$/.test(formData.code.trim().toUpperCase())) {
      setError('Le code (4 caractères alphanumériques) est obligatoire.');
      return;
    }
    if (phoneDisplay.trim() && (!phoneIsValid || !phoneE164)) {
      setError('Le numéro de téléphone est invalide pour le pays sélectionné.');
      return;
    }

    if (formData.createAccount) {
      if (!formData.password) {
        setError('Le mot de passe est obligatoire pour créer un compte.');
        return;
      }
      if (formData.password.length < 6) {
        setError('Le mot de passe doit contenir au moins 6 caractères.');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Les mots de passe ne correspondent pas.');
        return;
      }
    }

    const endpoint = formData.createAccount
      ? '/api/admin/employees/with-user'
      : '/api/admin/employees';

    try {
      setLoading(true);
      const body: Record<string, unknown> = {
        firstName:  formData.firstName,
        lastName:   formData.lastName,
        code:       formData.code.trim().toUpperCase(),
        email:      formData.email,
        phone:      phoneE164 || undefined,
        companyId:  formData.companyId || null,
        shopId:     formData.shopIds[0] || currentShopId || null,
        shopIds:    formData.shopIds.length ? formData.shopIds : (currentShopId ? [currentShopId] : []),
        type:       formData.type,
        notes:      formData.notes || null,
        isActive:   formData.isActive,
      };
      if (formData.createAccount) body.password = formData.password;

      const response = await apiClient(endpoint, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        const reason = data?.message || 'Erreur lors de la création';
        setError(reason);
        showToast(reason, 'error', 7000);
        return;
      }

      showToast(
        formData.createAccount
          ? `Compte employé créé — l'employé peut se connecter avec ${formData.email}`
          : 'Employé créé avec succès',
        'success',
        5000,
      );
      navigate('/employees');
    } catch (err) {
      console.error('Create employee error:', err);
      setError('Erreur de connexion au serveur');
      showToast('Erreur de connexion au serveur', 'error', 7000);
    } finally {
      setLoading(false);
    }
  };

  if (needsShopSelection) {
    return <ShopSelectionGate requireSingleShop />;
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/employees')}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour à la liste</span>
          </button>
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary">Nouvel employé</h1>
            <p className="text-textSecondary">Créer un profil employé avec accès à l'application</p>
          </div>
        </div>
        <button
          type="submit"
          form="employee-form"
          disabled={loading}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>{loading ? 'Enregistrement...' : 'Enregistrer'}</span>
        </button>
      </div>

      <ToastComponent />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-medium">{error}</p>
        </div>
      )}

      <form id="employee-form" onSubmit={handleSubmit} autoComplete="off" className="space-y-8">

        {/* Personal info */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
              <User className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Informations personnelles</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Prénom *</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => set('firstName', e.target.value)}
                className="input-field"
                placeholder="Jean"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Nom *</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => set('lastName', e.target.value)}
                className="input-field"
                placeholder="Dupont"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Code *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => set('code', e.target.value.toUpperCase())}
                className="input-field"
                maxLength={4}
                placeholder="JD01"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => set('email', e.target.value)}
                  className="input-field pl-9"
                  placeholder="jean.dupont@boutique.fr"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Téléphone</label>
              <InternationalPhoneInput
                value={phoneDisplay}
                country={phoneCountry}
                onChange={(result) => {
                  setPhoneDisplay(result.displayValue);
                  setPhoneCountry(result.country);
                  setPhoneE164(result.e164Value);
                  setPhoneIsValid(result.isValid);
                  set('phone', result.displayValue);
                  if (result.isValid) {
                    setError((prev) =>
                      prev?.startsWith('Le numéro de téléphone est invalide') ? null : prev,
                    );
                  }
                }}
                selectClassName="input-field"
                inputClassName="input-field"
                placeholder="+33 6 12 34 56 78"
              />
            </div>
          </div>
        </div>

        {/* Login account */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <KeyRound className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Compte de connexion</h2>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={formData.createAccount}
                onChange={(e) => set('createAccount', e.target.checked)}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded"
              />
              <span className="text-sm font-medium text-textPrimary">Créer un compte pour cet employé</span>
            </label>
          </div>

          {formData.createAccount ? (
            <div className="space-y-4">
              <div className="p-3 bg-primary-50 border border-primary-100 rounded-lg text-sm text-primary-700">
                L'employé pourra se connecter avec son email (<strong>{formData.email || '—'}</strong>) et ce mot de passe pour accéder au tableau de bord réception.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">Mot de passe *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => set('password', e.target.value)}
                      className="input-field pl-9 pr-10"
                      placeholder="Minimum 6 caractères"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">Confirmer le mot de passe *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => set('confirmPassword', e.target.value)}
                      className="input-field pl-9"
                      placeholder="Répétez le mot de passe"
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-textSecondary text-sm">
              L'employé sera créé sans compte de connexion. Vous pourrez en ajouter un ultérieurement depuis la fiche employé.
            </p>
          )}
        </div>

        {/* Association */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
              <Store className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Affectation</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Entreprise</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <select
                  value={formData.companyId}
                  onChange={(e) => set('companyId', e.target.value)}
                  className="input-field pl-9 appearance-none"
                >
                  <option value="">Aucune</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Boutiques</label>
              <div className="relative">
                <Store className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
                <select
                  value={formData.shopIds}
                  onChange={(e) => set('shopIds', Array.from(e.target.selectedOptions, (o) => o.value))}
                  className="input-field pl-9 min-h-[6rem]"
                  multiple
                >
                  {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <p className="text-xs text-textSecondary mt-1">Ctrl+clic pour sélectionner plusieurs boutiques</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Rôle</label>
              <select
                value={formData.type}
                onChange={(e) => set('type', e.target.value as any)}
                className="input-field"
              >
                <option value="VENDOR">Vendeur / Réceptionniste</option>
                <option value="SECRETARY">Secrétaire</option>
                <option value="OTHER">Autre</option>
              </select>
            </div>
          </div>
        </div>

        {/* Notes & status */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
              <FileText className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Notes & statut</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => set('notes', e.target.value)}
                rows={3}
                className="input-field"
                placeholder="Informations supplémentaires sur l'employé"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-sm font-medium text-textPrimary cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => set('isActive', e.target.checked)}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded"
              />
              Employé actif
            </label>
          </div>
        </div>
      </form>
    </div>
  );
}
